import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import HelpButton, {HelpButtonProps} from 'help/HelpButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {InputChangeEvent} from 'util/Types';

export function SettingsPopover({
  title,
  renderContent,
  placement,
}: Pick<HelpButtonProps, 'title' | 'renderContent' | 'placement'>): JSX.Element {
  return (
    <HelpButton
      title={title}
      renderContent={renderContent}
      placement={placement}
      interaction={FloatingInteraction.CLICK}
      iconClassName="tagSchemaSettings-helpIcon u-tiny-spacing-left"
    />
  );
}

interface SettingsLabelProps {
  id?: string;
  isRequired: boolean;
  helpIcon?: JSX.Element;
  label: string;
  name?: string;
  withBottomMargin: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export function SettingsLabel({
  id,
  label,
  name,
  isRequired,
  helpIcon,
  withBottomMargin,
  dataTestAttrs,
}: SettingsLabelProps): JSX.Element {
  return (
    <div
      className={classNames(
        'u-vertical-center-items',
        'settingsPageContainer-label',
        css(!withBottomMargin && styles.noBottomMargin)
      )}
      {...getTestProps(dataTestAttrs)}
    >
      <Label
        id={id}
        // Only use the parent div's bottom margin by overriding the label margin
        className={css(styles.noBottomMargin)}
        htmlFor={name}
      >
        {label}
        {isRequired && <span className="u-danger-color">*</span>}
      </Label>
      {helpIcon}
    </div>
  );
}

SettingsLabel.defaultProps = {
  isRequired: false,
  withBottomMargin: true,
};

export interface SettingsFieldProps {
  helpIcon?: JSX.Element;
  isBeingCreated: boolean;
  isMultiline?: boolean;
  isRequired: boolean;
  isWritable: boolean;
  label: string;
  name: string;
  onChange: (value: string | number) => void;
  error?: string;
  type: TextFieldType;
  /**
   * If true, displays URLs as hyperlinks when not editing.
   * @default false
   */
  urlAware?: boolean;
  /**
   * If false, submit button is disabled when attempting to change value to empty.
   * @default false
   */
  emptyValueAllowed?: boolean;
  textPlaceholder?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  withLabelBottomMargin: boolean;
  dataTestAttrs?: DataTestAttrs;
  maxLength?: number;
  /** Replaces the EditableField action buttons with the specified component */
  editableFieldSiblingComponent?: JSX.Element;
}

class SettingsField extends React.Component<SettingsFieldProps> {
  static defaultProps = {
    isRequired: false,
    type: TextFieldType.TEXT,
    urlAware: false,
    emptyValueAllowed: false,
    withLabelBottomMargin: true,
  };

  _handleChangeInput = (e: InputChangeEvent): void => {
    const value = e.target.value;
    this.props.onChange(value);
  };

  override render(): JSX.Element {
    const inputType = this.props.isMultiline ? TextFieldType.TEXTAREA : this.props.type;
    return (
      <FormGroup className="u-no-spacing-bottom">
        <SettingsLabel
          helpIcon={this.props.helpIcon}
          isRequired={this.props.isRequired}
          label={this.props.label}
          withBottomMargin={this.props.withLabelBottomMargin}
          // FormInput is an input or textarea under the hood - always labellable, so we use `for` on the
          // `SettingsLabel`. EditableTextField is a div under the hood - not labellable, so we point the
          // label association the other direction with `aria-labelledby`, and set an id on the label instead.
          {...(this.props.isBeingCreated ? {name: this.props.name} : {id: this.props.name})}
        />
        {this.props.isBeingCreated ? (
          <FormInput
            className="u-flex-grow"
            disabled={!this.props.isWritable}
            error={this.props.error}
            onChange={this._handleChangeInput}
            id={this.props.name}
            type={inputType}
            value={this.props.value}
            dataTestAttrs={this.props.dataTestAttrs}
            maxLength={this.props.maxLength}
          />
        ) : (
          <EditableTextField
            labelledBy={this.props.name}
            isDisabled={!this.props.isWritable}
            breakLines={this.props.isMultiline}
            requestChange={this.props.onChange}
            type={inputType}
            urlAware={this.props.urlAware}
            textPlaceholder={this.props.textPlaceholder}
            emptyValueAllowed={this.props.emptyValueAllowed}
            value={this.props.value}
            dataTestAttrs={this.props.dataTestAttrs}
            maxLength={this.props.maxLength}
            editorSiblingComponent={this.props.editableFieldSiblingComponent}
          />
        )}
      </FormGroup>
    );
  }
}

export default SettingsField;

const styles = StyleSheet.create({
  noBottomMargin: {
    marginBottom: 0,
  },
});
